import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faMicrophone, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Button,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@repo/videa-components/src/index';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { isToday } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';

import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import {
  useExistingNotes,
  useExistingStandalonePatientVisitNotes,
} from '@/react-query/videa-notes/useExistingNotes';
import { useHasWriteback } from '@/react-query/videa-notes/useHasWriteback';
import { usePrompts } from '@/react-query/videa-notes/usePrompts';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';
import { formatPatientFirstNameAndLastName } from '@/utils/patient';

import { usePreferences } from '../../react-query/usePreferences';
import { mixpanelEvents } from '../../utils/mixpanel';
import { selectDateAtom } from '../patientList/atoms';
import { getPatientName } from '../uploader/patientList/patientListTableContainer/utils';

import {
  appointmentDataAtom,
  appointmentDataWithResetAtom,
  availableAudioDevicesAtom,
  hasMicrophonePermissionAtom,
  isCancelRecordingConfirmationShownAtom,
  isRecorderSheetOpenAtom,
  mediaBlobUrlAtom,
  monitoringStreamAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
  selectedAudioDeviceIdAtom,
  uiStatusAtom,
  voiceNotesSourceAtom,
} from './atoms';
import LoadingNoteGeneration from './components/LoadingNoteGeneration';
import AudioRecorder from './components/microphone/AudioRecorder';
import MicrophoneSelector from './components/microphone/MicrophoneSelector';
import { NoteEditor } from './components/NoteEditor';
import { NoteGenerationDetails } from './components/NoteGenerationDetails';
import type { NoteGenerationErrorProps } from './components/NoteGenerationError';
import NoteGenerationError from './components/NoteGenerationError';
import { NoteReviewWarning } from './components/NoteReviewWarning';
import NoteSelect from './components/NoteSelect';
import { NotesPageNavigationBlurb } from './components/NotesPageNavigationBlurb';
import { PatientConsentBlurb } from './components/PatientConsentBlurb';
import { PermissionStateDetector } from './components/permissions';
import { PMSNoteActions } from './components/PMSNoteActions';
import PromptSelect from './components/PromptSelect';
import { QuickNotePatientForm } from './components/QuickNotePatientForm';
import { TooltipButton } from './components/TooltipButton';
import { useSaveNoteToPMS } from './hooks/useSaveNoteToPMS';
import { useVideaNotesAnalytics } from './hooks/useVideaNotesAnalytics';
import { buttonStyling } from './styles';
import { VideaNotesUIState } from './types';
import type { MarkConsentRequest } from './utils';
import {
  CHUNK_SIZE,
  PatientConsentStatus,
  completeUpload,
  convertMediaBlobToFile,
  createPatientConsentRecord,
  getPresignedUrls,
  initiateUpload,
  uploadFileParts,
} from './utils';

const BLURB_DELIMITER = '\n\n==========\n\n';

export const getNoteSummaryWithoutBlurb = (
  noteSummary: string,
  hasWritebackEnabled: boolean,
) => {
  return hasWritebackEnabled
    ? noteSummary
    : noteSummary.split(BLURB_DELIMITER)[0];
};

/**
 * Safely converts unifiedAppointmentId to a number or undefined.
 * Returns undefined if the value is undefined, null, empty string, or NaN.
 */
const safeUnifiedAppointmentId = (
  unifiedAppointmentId: string | undefined,
): number | undefined => {
  if (!unifiedAppointmentId || unifiedAppointmentId.trim() === '') {
    return undefined;
  }

  const num = Number(unifiedAppointmentId);
  return isNaN(num) ? undefined : num;
};

export const VideaNotes = () => {
  const queryClient = useQueryClient();

  // need these for nucleus-api to validate our request
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  const user = useSelector((state: any) => state?.login?.user);
  const displayPhi = useSelector((state: any) => state.login?.user?.privacyOn);

  // we'll use these in the clinical note sheet to provide the clinician
  // with context about whom they're recording a note
  const appointmentDataForVideaNotes = useAtomValue(appointmentDataAtom);

  const setAppointmentDataForVideaNotes = useSetAtom(
    appointmentDataWithResetAtom,
  );
  const patient = appointmentDataForVideaNotes?.patient;
  const estimatedVisitId = appointmentDataForVideaNotes?.estimatedVisitId;
  const unifiedAppointmentId = appointmentDataForVideaNotes?.unifiedId;
  const standalonePatientVisitId =
    appointmentDataForVideaNotes?.standalonePatientVisitId;

  const appointmentStartsAt = appointmentDataForVideaNotes?.appointmentStartsAt;

  const patientName = getPatientName(
    formatPatientFirstNameAndLastName(patient?.firstName, patient?.lastName),
    0,
    displayPhi,
  );

  const patientNameForConsentRecord = getPatientName(
    formatPatientFirstNameAndLastName(patient?.firstName, patient?.lastName),
    0,
    true,
  );

  // feature flag that gates this entire component
  const { data: preferences } = usePreferences();
  const isVideaNotesEnabled = preferences?.isVideaNotesEnabled;

  const selectedDate = useAtomValue(selectDateAtom);

  // treat the UI like a state machine, and store in global state
  const [uiStatus, setUiStatus] = useAtom(uiStatusAtom);
  const recorderStatus = useAtomValue(recorderStatusAtom);
  const [
    isCancelRecordingConfirmationShown,
    setIsCancelRecordingConfirmationShown,
  ] = useAtom(isCancelRecordingConfirmationShownAtom);

  /**
   * This represents the audio file we will be uploading, provided by
   * the AudioRecorder component.
   */
  const [mediaBlobUrl] = useAtom(mediaBlobUrlAtom);

  const [patientConsentRecordId, setPatientConsentRecordId] = useState<
    string | undefined
  >(undefined);

  const [isSheetOpen, setIsSheetOpen] = useAtom(isRecorderSheetOpenAtom);

  // Videa Notes Prompts, aka user Templates for generating notes
  const { data: promptOptions } = usePrompts();
  const [selectedPrompt, setSelectedPrompt] = useState<string | undefined>(
    undefined,
  );
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | undefined>(
    undefined,
  );

  const [patientConsentBlurb, setPatientConsentBlurb] = useState<
    string | undefined
  >(undefined);

  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [isSavingNoteUpdate, setIsSavingNoteUpdate] = useState(false);

  // Get microphone state from atoms
  const hasMicrophonePermission = useAtomValue(hasMicrophonePermissionAtom);
  const setMonitoringStream = useSetAtom(monitoringStreamAtom);
  const availableDevices = useAtomValue(availableAudioDevicesAtom);
  const selectedDeviceId = useAtomValue(selectedAudioDeviceIdAtom);
  const previewStream = useAtomValue(previewAudioStreamAtom);
  const isStreamDetected = previewStream?.active === true;

  // Get voice notes source from atoms
  const voiceNotesSource = useAtomValue(voiceNotesSourceAtom);

  // Mixpanel user event tracking
  const eventTrackingUserPayload = useMemo(
    () => ({
      patient: {
        imagingPatientId: patient?.imagingPatientId,
        pmsPatientId: patient?.pmsPatientId,
        videaId: patient?.videaId,
      },
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId: selectedDeviceId || '',
      isStreamDetected,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      template: selectedPrompt,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
      voiceNotesSource,
    }),
    [
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      selectedDate,
      selectedPrompt,
      patient,
      hasMicrophonePermission,
      availableDevices,
      selectedDeviceId,
      isStreamDetected,
      voiceNotesSource,
    ],
  );
  const { trackVideaNotesEvent } = useVideaNotesAnalytics(
    eventTrackingUserPayload,
  );

  const [processingError, setProcessingError] =
    useState<NoteGenerationErrorProps>();

  // the results from our request for summarizing the audio file
  const {
    data: unifiedAppointmentNotesData = [],
    error: unifiedAppointmentNotesError,
    isLoading: isLoadingClinicalNotes,
    refetch: refetchClinicalNotes,
  } = useExistingNotes(
    unifiedAppointmentId,
    isSheetOpen && !!unifiedAppointmentId,
  );

  const {
    data: standalonePatientVisitNotesData = [],
    error: standalonePatientVisitNotesError,
    isLoading: isLoadingStandalonePatientVisitNotes,
    refetch: refetchStandalonePatientVisitNotes,
  } = useExistingStandalonePatientVisitNotes(
    standalonePatientVisitId,
    isSheetOpen && !!standalonePatientVisitId,
  );

  const clinicalNotesData = useMemo(() => {
    // Only show notes from the appropriate source based on appointment type
    if (unifiedAppointmentId) {
      // For regular appointments, only show unified appointment notes
      return unifiedAppointmentNotesData;
    } else if (standalonePatientVisitId) {
      // For standalone visits, only show standalone patient visit notes
      return standalonePatientVisitNotesData;
    }
    // If neither, return empty array
    return [];
  }, [
    unifiedAppointmentId,
    standalonePatientVisitId,
    unifiedAppointmentNotesData,
    standalonePatientVisitNotesData,
  ]);

  // Detect if any writeback is in progress (clinicalNoteId exists but processedAt is undefined and no error)
  const isWritebackInProgress = useMemo(() => {
    return clinicalNotesData.some(
      (note) =>
        note.writebackStatus?.clinicalNoteId &&
        !note.writebackStatus?.processedAt &&
        note.writebackStatus?.errorCode === null,
    );
  }, [clinicalNotesData]);

  // Auto-save and writeback hooks
  const updateNotes = useUpdateNotes();

  const hasWriteback = useHasWriteback(isSheetOpen);
  const hasWritebackEnabled = Boolean(hasWriteback?.data);

  // Check if the current note has been saved to PMS (read-only)
  const isNoteSavedToPMS = useMemo(() => {
    return (
      selectedNote?.writebackStatus?.isProcessed &&
      selectedNote?.writebackStatus?.processedAt &&
      selectedNote?.writebackStatus?.errorCode === null
    );
  }, [selectedNote?.writebackStatus]);

  // Check if the note is currently being saved to PMS (also read-only during saving)
  const isNoteBeingSavedToPMS = useMemo(() => {
    return (
      selectedNote?.writebackStatus?.clinicalNoteId &&
      !selectedNote?.writebackStatus?.processedAt &&
      selectedNote?.writebackStatus?.errorCode === null
    );
  }, [selectedNote?.writebackStatus]);

  // Combined check for any state that should make the note read-only
  const isNoteReadOnly = isNoteSavedToPMS || isNoteBeingSavedToPMS;

  const [noteContent, setNoteContent] = useState(
    selectedNote?.editedSummary || selectedNote?.summary || '',
  );

  // Update noteContent when selectedNote changes
  useEffect(() => {
    const newContent =
      selectedNote?.editedSummary || selectedNote?.summary || '';
    setNoteContent(newContent);
  }, [selectedNote?.id, selectedNote?.editedSummary, selectedNote?.summary]);

  const { triggerSaveNoteToPMS, isSavingNoteToPMS } = useSaveNoteToPMS({
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedNoteId: selectedNote?.id ?? undefined,
    noteContent,
    hasWritebackEnabled,
  });

  const handleSaveNoteToPMS = () => {
    triggerSaveNoteToPMS();
    trackVideaNotesEvent(mixpanelEvents.videaNotesWritebackClick);
  };

  const {
    triggerSaveNoteToPMS: triggerRetryWriteback,
    isSavingNoteToPMS: isRetryingWriteback,
  } = useSaveNoteToPMS({
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
    selectedNoteId: selectedNote?.id ?? undefined,
    noteContent,
    hasWritebackEnabled,
  });

  const handleRetryWritebackToPMS = () => {
    triggerRetryWriteback();
    trackVideaNotesEvent(mixpanelEvents.videaNotesWritebackClick);
  };

  const prevContentRef = useRef(noteContent);

  const handleSelectedNote = (noteId: string | undefined) => {
    const matchedNote = clinicalNotesData.find((note) => note.id === noteId);
    setSelectedNote(matchedNote);
  };

  /**
   * This opens up the Videa Notes component and prompts the user
   * to start recording their audio. This is the initial "step"
   * of this workflow.
   */
  const handleSheetOpen = (e: Event) => {
    e.preventDefault();
    trackVideaNotesEvent(mixpanelEvents.videaNotesOpenSheet);
  };

  const handlePatientCreated = (visitData: {
    id: string;
    firstName: string;
    lastName: string | null;
    sourcePatientId: string | null;
    dateOfBirth: Date | null;
    appointmentDate: Date;
    appointmentStartTime: string;
  }) => {
    // Update appointment data with the created visit
    const appointmentData = {
      appointmentStartsAt: new Date(visitData.appointmentDate).toISOString(),
      patient: {
        firstName: visitData.firstName,
        lastName: visitData.lastName ?? undefined,
        displayName: `${visitData.firstName} ${visitData.lastName ?? ''}`,
        sourcePatientId: visitData.sourcePatientId ?? undefined,
      },
      estimatedVisitId: null,
      standalonePatientVisitId: visitData.id,
      unifiedId: undefined,
      loggedInUser: user.username ?? '',
    };

    setAppointmentDataForVideaNotes(appointmentData);

    // Transition to AUDIO_CAPTURE mode
    setUiStatus(VideaNotesUIState.AUDIO_CAPTURE);
  };

  /**
   * This provides a callback function for whenever the AudioRecorder
   * component starts the recording.
   */
  const handleStartRecording = async () => {
    try {
      if (preferences?.enableVoiceNotesConsent) {
        const consentRequest: MarkConsentRequest = {
          consentStatus: PatientConsentStatus.YES,
          isGivenByPatient: true,
          patientFullName: patientNameForConsentRecord,
          patientAppointmentAt: appointmentStartsAt ?? '',
          recordedBy: user.username,
          patientDob: patient?.dob,
        };
        const response = await createPatientConsentRecord({
          consentRequest,
          unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
          standalonePatientVisitId: standalonePatientVisitId,
          currentOrganizationId,
          currentPracticeId,
        });
        setPatientConsentRecordId(response.id);
      }
      trackVideaNotesEvent(mixpanelEvents.videaNotesStartRecorderClick);
    } catch (error) {
      console.error(error);
    }
  };

  const handleStartProviderOnlyRecording = async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NOT_REQUIRED,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: user.username,
        patientDob: patient?.dob,
      };
      const response = await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
      });
      setPatientConsentRecordId(response.id);
      trackVideaNotesEvent(
        mixpanelEvents.videaNotesStartProviderOnlyRecorderClick,
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handlePatientConsentDenied = async () => {
    try {
      const consentRequest: MarkConsentRequest = {
        consentStatus: PatientConsentStatus.NO,
        isGivenByPatient: true,
        patientFullName: patientNameForConsentRecord,
        patientAppointmentAt: appointmentStartsAt ?? '',
        recordedBy: user.username,
        patientDob: patient?.dob,
      };
      await createPatientConsentRecord({
        consentRequest,
        unifiedAppointmentId: safeUnifiedAppointmentId(unifiedAppointmentId),
        standalonePatientVisitId: standalonePatientVisitId,
        currentOrganizationId,
        currentPracticeId,
      });
      trackVideaNotesEvent(mixpanelEvents.videaNotesPatientConsentDenied);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * This provides a callback function for whenever the AudioRecorder
   * component pauses the recording.
   */
  const handlePause = () =>
    trackVideaNotesEvent(mixpanelEvents.videaNotesPauseClick);

  /**
   * This provides a callback function for whenever the AudioRecorder
   * component resumes a paused recording.
   */
  const handleResume = () =>
    trackVideaNotesEvent(mixpanelEvents.videaNotesResumeClick);

  /**
   * When the value of `mediaBlobUrl` is no longer `undefined`,
   * we can kick off the rest of the process, seen in `handleSave()`
   */
  const handleGenerateNote = () => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesClick);
    setUiStatus(VideaNotesUIState.NOTE_GENERATION);
    handleSave();
  };

  const handleGenerateNoteError = useCallback(() => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesGenerateNotesError);
  }, [trackVideaNotesEvent]);

  const handleCreateAdditionalNote = () => {
    setAppointmentDataForVideaNotes(null);
    setAppointmentDataForVideaNotes({
      appointmentStartsAt: appointmentStartsAt,
      patient: patient,
      estimatedVisitId: estimatedVisitId,
      unifiedId: unifiedAppointmentId,
      standalonePatientVisitId: standalonePatientVisitId,
      loggedInUser: user.username,
    });
    setUiStatus(VideaNotesUIState.AUDIO_CAPTURE);
  };

  /**
   * This converts the local audio blob, if present, into a File
   * and appends it to a FormData object. This format is expected
   * when submitting any form data to an API endpoint.
   */
  const handleSave = useCallback(async () => {
    const hasRequiredConsent =
      !preferences?.enableVoiceNotesConsent || patientConsentRecordId;

    if (mediaBlobUrl && hasRequiredConsent) {
      try {
        const startTime = new Date();

        /* Start Multipart Upload */
        const audioFile = await convertMediaBlobToFile(
          mediaBlobUrl,
          unifiedAppointmentId || standalonePatientVisitId,
        );

        /**
         * step 1: initiate multipart upload
         */
        const { key, uploadId } = await initiateUpload(
          audioFile.name,
          currentOrganizationId,
          currentPracticeId,
        );

        /**
         * step 2: get presigned URLs
         */
        const partCount = Math.ceil(audioFile.size / CHUNK_SIZE);
        const urls = await getPresignedUrls(
          key,
          uploadId,
          partCount,
          currentOrganizationId,
          currentPracticeId,
        );

        /**
         * step 3: upload each part
         */
        const etags = await uploadFileParts(audioFile, urls);

        /**
         * step 4: complete upload
         */
        const note = await completeUpload(
          key,
          uploadId,
          etags,
          patientConsentRecordId,
          unifiedAppointmentId,
          standalonePatientVisitId,
          selectedPrompt,
          currentOrganizationId,
          currentPracticeId,
        );

        // Only refetch the relevant notes based on appointment type
        let updatedNotesData;
        if (unifiedAppointmentId) {
          const refetchResult = await refetchClinicalNotes();
          updatedNotesData = refetchResult.data;
        }
        if (standalonePatientVisitId) {
          const refetchResult = await refetchStandalonePatientVisitNotes();
          updatedNotesData = refetchResult.data;
        }

        const calculatedLoadTime = new Date().getTime() - startTime.getTime();
        trackVideaNotesEvent(mixpanelEvents.videaNotesResultLoadTime, {
          loadTime: calculatedLoadTime,
        });

        // Invalidate ambient data queries to refresh the table with new note data
        queryClient.invalidateQueries({
          queryKey: [
            endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
          ],
        });
        queryClient.invalidateQueries({
          queryKey: [
            endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
          ],
        });

        // Set response - use the updated note from refetched data to ensure we have the latest data
        if (updatedNotesData && updatedNotesData.length > 0) {
          // Find the note that matches the ID from completeUpload
          const updatedNote =
            updatedNotesData.find((n) => n.id === note.id) ||
            updatedNotesData[0];
          setSelectedNote(updatedNote);
        } else {
          // Fallback to the note from completeUpload if refetch didn't work
          setSelectedNote(note);
        }

        setUiStatus(VideaNotesUIState.SUMMARY_RESULT);
      } catch (error: unknown) {
        if (!axios.isAxiosError(error)) {
          setProcessingError({
            title: 'An unexpected error occurred.',
            description: 'Please try again later.',
          });
        } else {
          // Axios-specific errors
          const description =
            error.response?.data?.data?.data?.detail ??
            'Please try again later';
          setProcessingError({
            title: 'There was an issue processing your audio recording.',
            description: description,
          });
        }
        setUiStatus(VideaNotesUIState.ERROR);
        handleGenerateNoteError();
      } finally {
        setIsSheetOpen(true);
      }

      /* End Multipart Upload */
    } else {
      console.error('mediaBlobUrl is undefined - unable to process audio');
    }
  }, [
    mediaBlobUrl,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentPracticeId,
    currentOrganizationId,
    patientConsentRecordId,
    selectedPrompt,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    queryClient,
    setUiStatus,
    setIsSheetOpen,
    handleGenerateNoteError,
    trackVideaNotesEvent,
    preferences?.enableVoiceNotesConsent,
  ]);

  const handleCancelRecording = () => {
    trackVideaNotesEvent(mixpanelEvents.videaNotesCancelRecorderClick);
  };

  const handleCancelRecordingConfirmation = () => {
    trackVideaNotesEvent(
      mixpanelEvents.videaNotesCancelRecorderConfirmationClick,
    );
  };

  const handleCloseOnErrorScreen = () => {
    setIsSheetOpen(false);
  };

  /**
   * This is triggered when the user closes the sheet, either by clicking
   * the X icon directly, or by clicking on any viewport space outside of
   * the sheet
   */
  const handleSheetOpenChange = () => {
    // Force save any pending changes before closing
    if (selectedNote?.id && prevContentRef.current !== noteContent) {
      updateNotes.mutate({
        clinicalNoteId: selectedNote.id,
        updateNotesBody: {
          editedSummary: getNoteSummaryWithoutBlurb(
            noteContent,
            hasWritebackEnabled,
          ),
        },
      });
    }

    setIsSheetOpen(false);

    /**
     * if the user clicks the Cancel button, but then navigates away
     * from the sheet without clicking Cancel Recording as a confirmation,
     * we are assuming the same intent as if the user has clicked the
     * Not Now button to abort cancelling the recording session
     */
    if (isCancelRecordingConfirmationShown) {
      setIsCancelRecordingConfirmationShown(false);
    }

    /**
     * track where in the process the user was before the closed the sheet
     */
    if (uiStatus === VideaNotesUIState.AUDIO_CAPTURE) {
      trackVideaNotesEvent(mixpanelEvents.videaNotesRecorderClose);
    } else if (uiStatus === VideaNotesUIState.SUMMARY_RESULT) {
      trackVideaNotesEvent(mixpanelEvents.videaNotesResultClose);
    }
  };

  // Mixpanel user event "click" trackers, no other side effects
  const handleSummaryTextAreaClick = () =>
    trackVideaNotesEvent(mixpanelEvents.videaNotesTextAreaClick);

  /**
   * This handles copying the summarized text into the local clipboard.
   *
   * @param copiedText - the summarized text
   */
  const handleCopyText = (copiedText: string) => {
    navigator.clipboard.writeText(copiedText);
    trackVideaNotesEvent(mixpanelEvents.videaNotesResultCopy);

    // Show tooltip temporarily
    setShowCopyTooltip(true);
    setTimeout(() => {
      setShowCopyTooltip(false);
    }, 2000); // Hide after 2 seconds
  };

  // Update local state when data is fetched
  useEffect(() => {
    if (Array.isArray(promptOptions) && promptOptions.length > 0) {
      setSelectedPrompt(promptOptions[0].name);
    }
  }, [promptOptions, setSelectedPrompt]);

  // Reset selectedNote when appointment changes
  useEffect(() => {
    setSelectedNote(undefined);
  }, [unifiedAppointmentId, standalonePatientVisitId]);

  // Handle updates to the selected note
  useEffect(() => {
    if (!Array.isArray(clinicalNotesData)) return;

    // Ensure the selected note is updated with the latest data
    if (selectedNote) {
      const updatedNote = clinicalNotesData.find(
        (note) => note.id === selectedNote.id,
      );
      if (updatedNote && !isEqual(updatedNote, selectedNote)) {
        setSelectedNote(updatedNote);
      }
    } else if (clinicalNotesData.length > 0 && !selectedNote) {
      // Always select the first (newest) note when notes are available and no note is selected
      // This ensures notes are automatically selected when opening from ambient table
      const noteToSelect = clinicalNotesData.reduce((latest, current) => {
        return new Date(current.updatedAt) > new Date(latest.updatedAt)
          ? current
          : latest;
      });
      setSelectedNote(noteToSelect);
    }
  }, [clinicalNotesData, selectedNote]);

  // Poll for updates when writeback is in progress
  useEffect(() => {
    if (isWritebackInProgress) {
      const interval = setInterval(() => {
        refetchClinicalNotes();
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [isWritebackInProgress, refetchClinicalNotes]);

  // Sync when the current note changes
  useEffect(() => {
    if (selectedNote !== undefined) {
      const summaryContent = selectedNote.editedSummary || selectedNote.summary;
      prevContentRef.current = summaryContent;

      const consent = selectedNote.patientConsent;
      if (consent && preferences?.enableVoiceNotesConsent) {
        let blurb = '';

        try {
          const createdAt = consent.createdAtISO
            ? new Date(consent.createdAtISO)
            : null;
          const isValidDate =
            createdAt instanceof Date && !isNaN(createdAt.getTime());

          const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const consentGrantedAt = isValidDate
            ? formatInTimeZone(
                createdAt,
                userTimeZone,
                'MMMM d, yyyy @ hh:mm:ss a zzz',
              )
            : 'An unknown date';

          blurb = `On ${consentGrantedAt}, ${consent.fullName} ${consent.dob ? `(DOB ${consent.dob})` : ''} gave verbal consent for audio recording for documentation during this encounter. Purpose explained and opt-out offered; consent granted.\nConsent Ref: [${consent.id}]`;
        } catch (e) {
          console.error('Error formatting consent date:', e);
          blurb = `Verbal consent for recording was granted, but the date could not be parsed. Consent Ref: [${consent.id}].`;
        }

        if (hasWritebackEnabled) {
          setPatientConsentBlurb(blurb);
          setNoteContent(summaryContent);
        } else {
          setPatientConsentBlurb(undefined);
          setNoteContent(`${summaryContent}${BLURB_DELIMITER}${blurb}`);
        }
      } else {
        setPatientConsentBlurb(undefined);
        setNoteContent(summaryContent);
      }
    }
  }, [selectedNote, hasWritebackEnabled, preferences?.enableVoiceNotesConsent]);

  // Simple debounced auto-save for note content changes
  useEffect(() => {
    if (!selectedNote?.id) return;

    // Only save if content actually changed
    const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );
    if (prevContentRef.current === noteWithoutBlurb) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsSavingNoteUpdate(true);
      updateNotes.mutate(
        {
          clinicalNoteId: selectedNote.id,
          updateNotesBody: {
            editedSummary: noteWithoutBlurb,
          },
        },
        {
          onSuccess: () => {
            // TODO: spin for 400ms regardless
            setIsSavingNoteUpdate(false);
            prevContentRef.current = noteWithoutBlurb;
          },
          onError: () => {
            setIsSavingNoteUpdate(false);
          },
        },
      );
    }, 1000); // 1 second delay

    return () => clearTimeout(timeoutId);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- updateNotes is stable and doesn't need to be in deps */
  }, [noteContent, selectedNote?.id]);

  // if this feature flag isn't enabled, don't render anything!
  if (!isVideaNotesEnabled) {
    return null;
  }

  return (
    <>
      <PermissionStateDetector onPermissionChange={() => {}} />
      <Sheet open={isSheetOpen} onOpenChange={handleSheetOpenChange}>
        <SheetContent
          onOpenAutoFocus={handleSheetOpen}
          className="vai-max-h-screen vai-flex vai-flex-col !vai-w-[60vw] vai-min-w-[800px] vai-z-[200] vai-font-inter vai-overflow-hidden"
          aria-describedby=""
        >
          <SheetHeader className="vai-flex vai-justify-between vai-bg-gray-100 vai-outline vai-outline-1 vai-outline-slate-300">
            <SheetTitle className="vai-font-inter vai-typography-p vai-text-zinc-700 vai-px-4 vai-py-3">
              <div className="vai-relative vai-flex vai-items-center vai-gap-4">
                <FontAwesomeIcon
                  className="vai-text-[#4A4F55]"
                  icon={faMicrophone}
                />
                <Trans>Voice Note</Trans>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div className="vai-flex-1 vai-overflow-hidden vai-px-4 vai-pb-4 vai-font-inter vai-flex vai-flex-col vai-min-h-0 vai-max-h-full">
            {/* loading and error states */}
            {((unifiedAppointmentId && isLoadingClinicalNotes) ||
              (standalonePatientVisitId &&
                isLoadingStandalonePatientVisitNotes)) && (
              <p>Loading notes...</p>
            )}

            {axios.isAxiosError(unifiedAppointmentNotesError) && (
              <p className="text-destructive">
                {unifiedAppointmentNotesError.response?.data?.message ??
                  'An error occurred'}
              </p>
            )}

            {axios.isAxiosError(standalonePatientVisitNotesError) && (
              <p className="text-destructive">
                {standalonePatientVisitNotesError.response?.data?.message ??
                  'An error occurred'}
              </p>
            )}

            {/* loading is finished, and we might have historical data */}
            {(() => {
              // Only check the relevant loading state based on whether we have a unified appointment or standalone visit
              const shouldCheckUnifiedLoading = !!unifiedAppointmentId;
              const shouldCheckStandaloneLoading = !!standalonePatientVisitId;

              const isRelevantLoadingComplete = shouldCheckUnifiedLoading
                ? !isLoadingClinicalNotes
                : shouldCheckStandaloneLoading
                  ? !isLoadingStandalonePatientVisitNotes
                  : true; // If neither, don't wait for loading

              return (
                isRelevantLoadingComplete &&
                (clinicalNotesData.length > 0 ||
                  !unifiedAppointmentId ||
                  !standalonePatientVisitId)
              );
            })() && (
              <>
                <div className="vai-relative vai-mb-4 vai-overflow-y-scroll">
                  <div className="vai-flex vai-pt-1 vai-items-center vai-justify-between vai-w-full">
                    <div className="vai-flex vai-flex-col vai-w-1/3">
                      <p className="vai-text-xl vai-text-black">
                        {patientName}
                      </p>
                    </div>

                    {/* prompt selection when beginning a recording */}
                    {uiStatus === VideaNotesUIState.AUDIO_CAPTURE &&
                      !mediaBlobUrl && (
                        <div className="vai-flex vai-justify-end vai-w-full vai-gap-1">
                          <div className="vai-w-1/2 vai-pr-1">
                            <MicrophoneSelector
                              onMonitoringStreamChange={setMonitoringStream}
                            />
                          </div>
                          {(recorderStatus === 'stopped' ||
                            recorderStatus === 'idle') && (
                            <div className="vai-w-1/2 vai-pr-1">
                              <PromptSelect
                                promptOptions={
                                  promptOptions !== undefined
                                    ? promptOptions
                                    : []
                                }
                                selectedPrompt={selectedPrompt}
                                onSelectedPrompt={setSelectedPrompt}
                              />
                            </div>
                          )}
                        </div>
                      )}

                    {/* list of existing notes when viewing summary results */}
                    {uiStatus === VideaNotesUIState.SUMMARY_RESULT &&
                      (recorderStatus === 'stopped' ||
                        recorderStatus === 'idle') &&
                      !mediaBlobUrl &&
                      clinicalNotesData.length > 0 && (
                        <div className="vai-w-1/2 vai-pr-1">
                          <NoteSelect
                            clinicalNotes={clinicalNotesData}
                            selectedNote={selectedNote}
                            onSelectedNote={handleSelectedNote}
                          />
                        </div>
                      )}
                  </div>

                  {uiStatus === VideaNotesUIState.PATIENT_INFO && (
                    <QuickNotePatientForm
                      onPatientCreated={handlePatientCreated}
                      onCancel={() => setIsSheetOpen(false)}
                    />
                  )}

                  {uiStatus === VideaNotesUIState.AUDIO_CAPTURE && (
                    <div className="vai-flex vai-flex-col vai-items-center vai-py-2 vai-w-full">
                      <AudioRecorder
                        onCancelRecording={handleCancelRecording}
                        onCancelRecordingConfirmation={
                          handleCancelRecordingConfirmation
                        }
                        onPauseRecording={handlePause}
                        onResumeRecording={handleResume}
                        onStartRecording={handleStartRecording}
                        onStartProviderOnlyRecording={
                          handleStartProviderOnlyRecording
                        }
                        onGenerateNote={handleGenerateNote}
                        onPatientConsentDenied={handlePatientConsentDenied}
                        isPromptSelected={selectedPrompt !== undefined}
                      />
                    </div>
                  )}

                  {uiStatus === VideaNotesUIState.NOTE_GENERATION && (
                    <LoadingNoteGeneration />
                  )}

                  {uiStatus === VideaNotesUIState.ERROR &&
                    processingError !== undefined && (
                      <div className="vai-flex vai-flex-col vai-px-1">
                        <NoteGenerationError
                          title={processingError.title}
                          description={processingError.description}
                        />
                        <div className="vai-mt-4 vai-pb-3 vai-self-start">
                          <Button
                            onClick={handleCloseOnErrorScreen}
                            variant="destructive"
                          >
                            <span>Dismiss</span>
                          </Button>
                        </div>
                      </div>
                    )}

                  {uiStatus === VideaNotesUIState.SUMMARY_RESULT &&
                    clinicalNotesData &&
                    selectedNote && (
                      <div className="vai-mt-4 vai-flex vai-flex-col vai-flex-1 vai-min-h-0 vai-overflow-hidden">
                        <div className="vai-flex vai-flex-col vai-space-y-4 vai-px-1 vai-flex-1 vai-min-h-0 vai-overflow-hidden">
                          <NoteReviewWarning />

                          <div className="vai-flex-1 vai-min-h-0 vai-overflow-hidden">
                            <NoteEditor
                              noteContent={noteContent}
                              isNoteReadOnly={isNoteReadOnly}
                              isNoteSavedToPMS={isNoteSavedToPMS}
                              isNoteBeingSavedToPMS={isNoteBeingSavedToPMS}
                              isSavingNoteUpdate={isSavingNoteUpdate}
                              onNoteChange={(value) => setNoteContent(value)}
                              onNoteClick={handleSummaryTextAreaClick}
                            />
                          </div>
                        </div>

                        {patientConsentBlurb &&
                          preferences.enableVoiceNotesConsent && (
                            <div className="vai-my-3 vai-flex-shrink-0 vai-px-1">
                              <PatientConsentBlurb
                                blurb={patientConsentBlurb}
                              />
                            </div>
                          )}

                        <div className="vai-flex vai-items-center vai-justify-between vai-w-full vai-pb-4 vai-pt-6 vai-px-1 vai-flex-shrink-0 vai-bg-white">
                          {/* Left: Navigation Blurb */}
                          <NotesPageNavigationBlurb
                            voiceNotesSource={voiceNotesSource}
                            onCloseSheet={() => setIsSheetOpen(false)}
                          />

                          {/* Right: Action Buttons */}
                          <div className="vai-space-x-2 vai-pr-1 vai-ml-auto">
                            {hasWritebackEnabled &&
                            patient !== undefined &&
                            patient.pmsPatientId != null ? (
                              <>
                                <TooltipButton
                                  icon={faPlus}
                                  tooltip="Start new recording"
                                  onClick={handleCreateAdditionalNote}
                                  variant="outline"
                                ></TooltipButton>
                                <TooltipButton
                                  icon={faCopy}
                                  tooltip="Copy note to clipboard"
                                  onClick={() => handleCopyText(noteContent)}
                                  variant="outline"
                                ></TooltipButton>
                                <PMSNoteActions
                                  patient={patient}
                                  selectedNote={selectedNote}
                                  handleSaveNoteToPMS={handleSaveNoteToPMS}
                                  isSavingNoteToPMS={isSavingNoteToPMS}
                                  handleRetryWritebackToPMS={
                                    handleRetryWritebackToPMS
                                  }
                                  isRetryingWriteback={isRetryingWriteback}
                                />
                              </>
                            ) : (
                              <div className="vai-relative vai-inline-block">
                                <Button
                                  onClick={handleCreateAdditionalNote}
                                  variant="outline"
                                >
                                  <span>
                                    <FontAwesomeIcon icon={faPlus} />
                                  </span>
                                  New Note
                                </Button>
                                <Button
                                  onClick={() => handleCopyText(noteContent)}
                                  className={`${buttonStyling} hover:vai-ring-primary/50 vai-ml-2`}
                                >
                                  <span>
                                    <FontAwesomeIcon icon={faCopy} />
                                  </span>
                                  Copy Note
                                </Button>
                                {showCopyTooltip && (
                                  <div className="vai-absolute vai-bottom-full vai-left-1/2 vai-transform vai--translate-x-1/2 vai-mb-1 vai-px-2 vai-py-1 vai-bg-gray-800 vai-text-white vai-text-xs vai-rounded vai-whitespace-nowrap vai-z-50 vai-pointer-events-none">
                                    <Trans>Copied!</Trans>
                                    <div className="vai-absolute vai-top-full vai-left-1/2 vai-transform vai--translate-x-1/2 vai-border-2 vai-border-transparent vai-border-t-gray-800"></div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
